import { Link } from "@tanstack/react-router";
import type { HijriEvent } from "@/lib/calendar/hijri-events";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

/**
 * Reusable Hijri event badge with tooltip (descId).
 *
 * Two modes:
 * - Compact pill (default): renders just the event name with ✦ icon.
 *   Used in monthly/yearly tables and DateHeaderTiles.
 * - Linked row (when `hYear` + `children` provided): wraps children in a
 *   <Link> to the Hijri date page. Used in HijriMonthPage event lists.
 */
export function HijriEventBadge({
  event,
  size = "sm",
  className,
  hYear,
  children,
}: {
  event: HijriEvent;
  size?: "xs" | "sm";
  className?: string;
  hYear?: number;
  children?: ReactNode;
}) {
  if (hYear !== undefined && children) {
    return (
      <Link
        to={`/kalender-hijriah/${hYear}/${event.hMonth}/${event.hDay}/` as never}
        className={cn("block", className)}
        title={event.descId}
        aria-label={`${event.name}: ${event.descId}`}
      >
        {children}
      </Link>
    );
  }
  const sizeCls = size === "xs"
    ? "px-1 py-0 text-[9px]"
    : "px-1.5 py-0.5 text-[10px]";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded bg-hari-penting/15 font-medium text-hari-penting",
        sizeCls,
        className,
      )}
      title={event.descId}
      aria-label={`${event.name}: ${event.descId}`}
    >
      <span aria-hidden>✦</span>
      <span className="truncate">{event.name}</span>
    </span>
  );
}